import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner, Form, ButtonGroup, Dropdown, Badge } from 'react-bootstrap'
import { getPurchaseOrders, deletePurchaseOrder, downloadPODocument } from '../api/poAPI'
import { vendorAPI } from '../api/vendorAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { formatDate, formatDateTime, highlightText, formatCurrency } from '../utils/commonUtils'
import ConfirmationModal from '../components/ConfirmationModal'
import PaginationComponent from '../components/Pagination'
import { handleSearchWithDebounce, useTableState } from '../utils/tableUtils'
import ResizableTable from '../components/ResizableTable'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'

function PurchaseOrdersListScreen() {
    const { hasPermission } = usePermissions()
    const [purchaseOrders, setPurchaseOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [vendorMap, setVendorMap] = useState({})
    const [warehouseMap, setWarehouseMap] = useState({})
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const navigate = useNavigate()
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [itemToDelete, setItemToDelete] = useState(null)
    const [selectedVendor, setSelectedVendor] = useState('')
    const [selectedStatus, setSelectedStatus] = useState('')
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const debounceTimeoutRef = useRef(null)
    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null
    })
    const [displayedDateRange, setDisplayedDateRange] = useState({ startDate: null, endDate: null })
    const [shouldFetchData, setShouldFetchData] = useState(0)

    const initialTableState = {
        visibility: {
            id: true,
            vendor: true,
            dateCreated: true,
            desiredDeliveryDate: true,
            vendorEta: true,
            lastReceivedDate: true,
            shipTo: true,
            totalAmount: true,
            status: true
        },
        widths: {
            actions: 150,
            id: 100,
            vendor: 200,
            dateCreated: 150,
            desiredDeliveryDate: 150,
            vendorEta: 150,
            lastReceivedDate: 150,
            shipTo: 200,
            totalAmount: 150,
            status: 120
        }
    }

    const { columnState, updateColumnVisibility, updateColumnWidth } = useTableState('purchase_orders', initialTableState)
    const columnVisibility = columnState.visibility

    const columnLabels = {
        id: 'PO #',
        vendor: 'Vendor',
        dateCreated: 'Date Created',
        desiredDeliveryDate: 'Requested Delivery',
        vendorEta: 'Vendor ETA',
        lastReceivedDate: 'Last Received',
        shipTo: 'Ship To',
        totalAmount: 'Total Amount',
        status: 'Status'
    }

    useEffect(() => {
        async function fetchPurchaseOrders() {
            try {
                setLoading(true)
                const response = await getPurchaseOrders(
                    currentPage,
                    pageSize,
                    searchTerm,
                    selectedVendor,
                    dateRange.startDate?.toISOString().split('T')[0],
                    dateRange.endDate?.toISOString().split('T')[0],
                    selectedStatus
                )
                setPurchaseOrders(response.results)
                setTotalPages(response.count)
            } catch (error) {
                console.error('Error fetching purchase orders:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchPurchaseOrders()
    }, [currentPage, searchTerm, selectedVendor, selectedStatus, shouldFetchData, pageSize])

    useEffect(() => {
        async function fetchVendors() {
            try {
                const response = await vendorAPI.getAll()
                const vendors = Array.isArray(response.results) ? response.results : 
                              Array.isArray(response) ? response : []
                const mapping = vendors.reduce((acc, vendor) => {
                    if (vendor && vendor._id && vendor.name) {
                        acc[vendor._id] = vendor.name
                    }
                    return acc
                }, {})
                setVendorMap(mapping)
            } catch (error) {
                console.error('Error fetching vendors:', error)
                setVendorMap({}) // Set empty map on error
            }
        }
        fetchVendors()
    }, [])

    useEffect(() => {
        async function fetchWarehouses() {
            try {
                const response = await warehouseAPI.getAll()
                const warehouses = Array.isArray(response.results) ? response.results : 
                                 Array.isArray(response) ? response : []
                const mapping = warehouses.reduce((acc, warehouse) => {
                    if (warehouse && warehouse._id && warehouse.name) {
                        acc[warehouse._id] = warehouse.name
                    }
                    return acc
                }, {})
                setWarehouseMap(mapping)
            } catch (error) {
                console.error('Error fetching warehouses:', error)
                setWarehouseMap({}) // Set empty map on error
            }
        }
        fetchWarehouses()
    }, [])

    const handleDelete = async (e, po) => {
        e.stopPropagation()
        e.preventDefault()
        setItemToDelete(po)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (itemToDelete) {
            try {
                await deletePurchaseOrder(itemToDelete._id)
                setPurchaseOrders(purchaseOrders.filter(po => po._id !== itemToDelete._id))
                setShowDeleteModal(false)
                setItemToDelete(null)
            } catch (error) {
                console.error('Error deleting purchase order:', error)
                const errorMessage = error.response?.data?.detail || 
                                   error.response?.data?.message ||
                                   error.message ||
                                   'An unexpected error occurred'
                alert(`Failed to delete purchase order: ${errorMessage}`)
                setShowDeleteModal(false)
                setItemToDelete(null)
            }
        }
    }


    const handleVendorFilterChange = (vendorId) => {
        setSelectedVendor(vendorId)
        setCurrentPage(1)
    }

    const handleStatusFilterChange = (statusKey) => {
        setSelectedStatus(statusKey)
        setCurrentPage(1)
    }

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        )
    }

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber)
    }


    const columns = [
        columnVisibility.id && {
            id: 'id',
            header: 'PO #',
            cell: (row) => highlightText(row.po_number || row._id, searchTerm)
        },
        columnVisibility.vendor && {
            id: 'vendor',
            header: () => (
                <Dropdown onSelect={handleVendorFilterChange}>
                    <Dropdown.Toggle 
                        variant="light" 
                        id="vendor-filter-dropdown"
                        style={{
                            fontWeight: 'bold',
                            padding: '0rem 0.5rem 0.35rem',
                            height: '25px'
                        }}
                    >
                        {selectedVendor ? vendorMap[selectedVendor] : 'Vendor'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item eventKey="">All Vendors</Dropdown.Item>
                        {Object.entries(vendorMap).map(([id, name]) => (
                            <Dropdown.Item key={id} eventKey={id}>
                                {name}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            ),
            cell: (row) => vendorMap[row.vendor] || 'N/A'
        },
        columnVisibility.dateCreated && {
            id: 'dateCreated',
            header: () => (
                <Dropdown>
                    <Dropdown.Toggle 
                        variant="light" 
                        id="date-filter-dropdown"
                        style={{
                            fontWeight: 'bold',
                            padding: '0rem 0.5rem 0.35rem',
                            height: '25px'
                        }}
                    >
                        {displayedDateRange.startDate || displayedDateRange.endDate ? 
                            `${formatDate(displayedDateRange.startDate)} - ${formatDate(displayedDateRange.endDate)}` : 
                            'Date Created'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ padding: '10px', minWidth: '450px' }}>
                        <div 
                            role="presentation"
                            onClick={e => e.stopPropagation()}
                            onKeyDown={e => e.stopPropagation()}
                        >
                            <div className="d-flex align-items-center gap-3">
                                <div className="d-flex align-items-center gap-2">
                                    <small>Start:</small>
                                    <DatePicker
                                        selected={dateRange.startDate}
                                        onChange={date => {
                                            setDateRange(prev => ({ ...prev, startDate: date }))
                                        }}
                                        selectsStart
                                        startDate={dateRange.startDate}
                                        endDate={dateRange.endDate}
                                        maxDate={new Date()}
                                        className="form-control form-control-sm"
                                        placeholderText="Select start date"
                                        isClearable
                                        shouldCloseOnSelect={true}
                                        showMonthDropdown
                                        showYearDropdown
                                        scrollableYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                                <div style={{ width: '10px' }}></div>
                                <div className="d-flex align-items-center gap-2">
                                    <small>End:</small>
                                    <DatePicker
                                        selected={dateRange.endDate}
                                        onChange={date => {
                                            setDateRange(prev => ({ ...prev, endDate: date }))
                                        }}
                                        selectsEnd
                                        startDate={dateRange.startDate}
                                        endDate={dateRange.endDate}
                                        minDate={dateRange.startDate}
                                        className="form-control form-control-sm"
                                        placeholderText="Select end date"
                                        isClearable
                                        maxDate={new Date()}
                                        shouldCloseOnSelect={true}
                                        showMonthDropdown
                                        showYearDropdown
                                        scrollableYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                            </div>
                            <div style={{ height: '10px' }}></div>
                            <div className="mt-2">
                                <Dropdown>
                                    <Dropdown.Toggle 
                                        variant="outline-secondary" 
                                        size="sm"
                                        id="quick-date-select"
                                    >
                                        Quick Select
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item 
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                const today = new Date()
                                                const lastWeek = new Date(today)
                                                lastWeek.setDate(today.getDate() - 7)
                                                setDateRange({
                                                    startDate: lastWeek,
                                                    endDate: today
                                                })
                                                e.target.closest('.dropdown').querySelector('.dropdown-toggle').click()
                                            }}
                                        >
                                            Last Week
                                        </Dropdown.Item>
                                        <Dropdown.Item 
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                const today = new Date()
                                                const twoWeeksAgo = new Date(today)
                                                twoWeeksAgo.setDate(today.getDate() - 14)
                                                setDateRange({
                                                    startDate: twoWeeksAgo,
                                                    endDate: today
                                                })
                                                e.target.closest('.dropdown').querySelector('.dropdown-toggle').click()
                                            }}
                                        >
                                            Last 2 Weeks
                                        </Dropdown.Item>
                                        <Dropdown.Item 
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                const today = new Date()
                                                const lastMonth = new Date(today)
                                                lastMonth.setMonth(today.getMonth() - 1)
                                                setDateRange({
                                                    startDate: lastMonth,
                                                    endDate: today
                                                })
                                                e.target.closest('.dropdown').querySelector('.dropdown-toggle').click()
                                            }}
                                        >
                                            Last Month
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                            <div className="d-flex gap-2 mt-2 justify-content-between">
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setDateRange({ startDate: null, endDate: null })
                                        }}
                                    >
                                        Clear Dates
                                    </Button>

                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm"
                                        onClick={(e) => {
                                            e.target.closest('.dropdown').querySelector('.dropdown-toggle').click()
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                                <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={(e) => {
                                        e.target.closest('.dropdown').querySelector('.dropdown-toggle').click()
                                        setShouldFetchData(prev => prev + 1)
                                        setDisplayedDateRange(dateRange)
                                    }}
                                >
                                    Set
                                </Button>
                            </div>
                        </div>
                    </Dropdown.Menu>
                </Dropdown>
            ),
            cell: (row) => formatDate(row.date_created)
        },
        columnVisibility.desiredDeliveryDate && {
            id: 'desiredDeliveryDate',
            header: 'Requested Delivery',
            cell: (row) => {
                if (row.desired_delivery_date) {
                    return formatDate(row.desired_delivery_date)
                }
                return row.desired_delivery_text || ''
            }
        },
        columnVisibility.vendorEta && {
            id: 'vendorEta',
            header: 'Vendor ETA',
            cell: (row) => {
                if (row.vendor_eta) {
                    return formatDate(row.vendor_eta)
                }
                return ''
            }
        },
        columnVisibility.lastReceivedDate && {
            id: 'lastReceivedDate',
            header: 'Last Received',
            cell: (row) => {
                if (row.last_received_date) {
                    return formatDate(row.last_received_date)
                }
                return ''
            }
        },
        columnVisibility.shipTo && {
            id: 'shipTo',
            header: 'Ship To',
            cell: (row) => highlightText(warehouseMap[row.ship_to] || 'N/A', searchTerm)
        },
        columnVisibility.totalAmount && {
            id: 'totalAmount',
            header: 'Total Amount',
            cell: (row) => {
                const formattedAmount = formatCurrency(row.total_amount)
                // Also search in the raw number format
                const rawAmount = row.total_amount.toString()
                return highlightText(formattedAmount, searchTerm) ||
                    (rawAmount.includes(searchTerm) ? 
                        highlightText(formattedAmount, rawAmount) : 
                        formattedAmount)
            }
        },
        columnVisibility.status && {
            id: 'status',
            header: () => (
                <Dropdown onSelect={handleStatusFilterChange}>
                    <Dropdown.Toggle 
                        variant="light" 
                        id="status-filter-dropdown"
                        style={{
                            fontWeight: 'bold',
                            padding: '0rem 0.5rem 0.35rem',
                            height: '25px'
                        }}
                    >
                        {selectedStatus === 'completed' ? 'Completed' : selectedStatus === 'active' ? 'Active' : selectedStatus === 'confirmed' ? 'Confirmed' : 'Status'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item eventKey="">All Statuses</Dropdown.Item>
                        <Dropdown.Item eventKey="active">Active</Dropdown.Item>
                        <Dropdown.Item eventKey="confirmed">Confirmed</Dropdown.Item>
                        <Dropdown.Item eventKey="completed">Completed</Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            ),
            cell: (row) => {
                // Priority: Completed > Confirmed > Active
                const badgeVariant = row.is_completed ? 'success' : row.is_confirmed ? 'info' : 'secondary'
                const badgeText = row.is_completed ? 'Completed' : row.is_confirmed ? 'Confirmed' : 'Active'

                return (
                    <Badge
                        bg={badgeVariant}
                        className="text-white"
                    >
                        {badgeText}
                    </Badge>
                )
            }
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: (row) => (
                <ButtonGroup size="sm" className="gap-1">
                    {hasPermission('po.view') && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                navigate(`/purchase-order/${row._id}`, { state: { update: true } })
                            }}
                        >
                            Details
                        </Button>
                    )}
                    {hasPermission('po.view') && (
                        <Button
                            variant="info"
                            size="sm"
                            onClick={async (e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                try {
                                    await downloadPODocument(row._id)
                                } catch (error) {
                                    console.error('Error downloading document:', error)
                                    alert('Failed to download document')
                                }
                            }}
                        >
                            Download
                        </Button>
                    )}
                    {hasPermission('po.modify') && (
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={(e) => handleDelete(e, row)}
                            // disabled={row.is_completed}
                            // title={row.is_completed ? "Cannot delete completed PO" : "Delete PO"}
                            title="Delete PO"
                        >
                            Delete
                        </Button>
                    )}
                </ButtonGroup>
            )
        }
    ].filter(Boolean)

    return (
        <Container fluid className="px-4 py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Purchase Orders</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search by PO #, vendor, ship to, or total amount..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                {hasPermission('po.modify') && (
                    <Col xs="auto">
                        <Button
                            onClick={() => navigate('/purchase-order/create')}
                            variant="success"
                        >
                            Create Purchase Order
                        </Button>
                    </Col>
                )}
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <div style={{ overflowX: 'auto', minWidth: '100%' }}>
                        <ResizableTable
                            columns={columns}
                            data={purchaseOrders}
                            initialColumnWidths={columnState.widths}
                            tableId="purchase_orders"
                            striped={true}
                            hover={true}
                            className="table-sm"
                        />
                    </div>
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}

            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Confirm Deletion"
                message="Are you sure you want to delete this purchase order?"
            />
        </Container>
    )
}

export default PurchaseOrdersListScreen 